import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Junior practical for "How the Internet Works".
 *
 * Three small things rather than one big one, because this is for
 * seven to twelve year olds and a long task loses them.
 *
 *   Put it in order  - the journey as five cards to arrange. Gets it
 *                      wrong cheerfully and says which one moved.
 *   Send it          - type a message, watch it become packets, watch
 *                      them arrive out of order, put them back.
 *   Locked or not?   - four situations, padlock or no padlock. The
 *                      point the lesson ends on, made concrete.
 *
 * No score is ever negative and nothing is timed. A child who gets it
 * wrong is told what to look at, not how many they missed.
 */

const STORE_KEY = 'cybercub.lab.message-journey';

/* ------------------------------------------------------------------ */
/* 1. put the journey in order                                        */
/* ------------------------------------------------------------------ */

const JOURNEY = [
  { id: 'j1', icon: '✍️', label: 'You type a message and press send' },
  { id: 'j2', icon: '📶', label: 'It goes to the little box in your house — the router' },
  { id: 'j3', icon: '🌊', label: 'It travels down wires, maybe under the sea' },
  { id: 'j4', icon: '🖥️', label: 'A server keeps a copy, so it is never lost' },
  { id: 'j5', icon: '🎉', label: 'It arrives on your friend’s screen' },
];

const shuffled = () => {
  const a = [...JOURNEY];
  // A fixed jumble rather than a random one, so a child who tries
  // again sees the same puzzle and can finish what they started.
  return [a[2], a[0], a[4], a[1], a[3]];
};

/* ------------------------------------------------------------------ */
/* 3. locked or not?                                                  */
/* ------------------------------------------------------------------ */

const LOCKS = [
  {
    id: 'k1',
    icon: '🏦',
    text: 'You are on a website and there is a little padlock next to its name.',
    answer: 0,
    why: 'The padlock means your messages are scrambled on the way. Other computers carry them but cannot read them.',
  },
  {
    id: 'k2',
    icon: '⚠️',
    text: 'A website says "Not secure" where the padlock should be.',
    answer: 1,
    why: 'Nothing is scrambled here. Never type a password or anything private into a page like this one.',
  },
  {
    id: 'k3',
    icon: '☕',
    text: 'You are using the free wifi in a café.',
    answer: 2,
    why: 'It depends on the website, not the wifi! On a café network lots of people share the same wires — so the padlock matters even more than usual.',
  },
  {
    id: 'k4',
    icon: '🔒',
    text: 'Somebody says "the padlock means this website is safe and honest".',
    answer: 1,
    why: 'Careful — that is not quite right. The padlock only means nobody can read your message on the way. A tricky website can have a padlock too.',
    tricky: true,
  },
];

const LOCK_OPTIONS = ['🔒 Safe to send private things', '🚫 Do not send private things', '🤔 It depends'];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function MessageJourneyLab() {
  const saved = useMemo(load, []);

  const [tab, setTab] = useState('order');
  const [cards, setCards] = useState(shuffled);
  const [checked, setChecked] = useState(false);
  const [orderDone, setOrderDone] = useState(Boolean(saved.orderDone));

  const [text, setText] = useState('hello!');
  const [sent, setSent] = useState(false);
  const [rebuilt, setRebuilt] = useState([]);
  const [sendDone, setSendDone] = useState(Boolean(saved.sendDone));

  const [lockPicks, setLockPicks] = useState(saved.lockPicks ?? {});

  useEffect(() => { document.title = 'Send a message · CyberCub Lab'; }, []);
  useEffect(() => { save({ orderDone, sendDone, lockPicks }); }, [orderDone, sendDone, lockPicks]);

  /* ---- ordering ---- */
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cards.length) return;
    const next = [...cards];
    [next[i], next[j]] = [next[j], next[i]];
    setCards(next);
    setChecked(false);
  };

  const rightOrder = cards.every((c, i) => c.id === JOURNEY[i].id);
  const check = () => {
    setChecked(true);
    if (rightOrder) setOrderDone(true);
  };

  /* ---- packets ---- */
  const packets = useMemo(() => {
    const t = text.trim() || 'hello!';
    const size = Math.max(1, Math.ceil(t.length / 4));
    const out = [];
    for (let i = 0; i < t.length; i += size) out.push({ n: out.length + 1, part: t.slice(i, i + size) });
    return out;
  }, [text]);

  // The jumbled arrival order: packets genuinely do not arrive in order.
  const arriving = useMemo(() => {
    const a = [...packets];
    return a.length > 2 ? [a[1], a[a.length - 1], a[0], ...a.slice(2, a.length - 1)] : a;
  }, [packets]);

  const addPacket = (p) => {
    if (rebuilt.some((r) => r.n === p.n)) return;
    const next = [...rebuilt, p];
    setRebuilt(next);
    if (next.length === packets.length && next.every((r, i) => r.n === i + 1)) setSendDone(true);
  };

  const rebuiltRight = rebuilt.length === packets.length && rebuilt.every((r, i) => r.n === i + 1);

  const locksDone = LOCKS.every((l) => lockPicks[l.id] === l.answer);

  useFinished('/lab/message-journey', locksDone);

  const TABS = [
    { id: 'order', n: 1, label: 'Put it in order', locked: false },
    { id: 'send', n: 2, label: 'Send one yourself', locked: !orderDone },
    { id: 'locks', n: 3, label: 'Locked or not?', locked: !sendDone },
  ];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Send a message</strong>
            <span className="lab-sub">Practical · Cyber Smart Kids, Module 1</span>
          </div>
        </div>
        <Link to="/learn/lesson/l1-how-the-internet-works" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lab-tab ${tab === t.id ? 'is-active' : ''} ${t.locked ? 'is-locked' : ''}`}
            onClick={() => !t.locked && setTab(t.id)}
            disabled={t.locked}
            aria-current={tab === t.id ? 'page' : undefined}
          >
            <span className="lab-tab-n">{t.n}</span>
            {t.label}
            {t.locked && <span aria-label="locked"> 🔒</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- 1. order ---------------- */}
      {tab === 'order' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Put the journey in order</h2>
              <p className="lab-lead">
                These five things all happen when you send a message — but they are jumbled up. Use
                the arrows to put them in the right order.
              </p>
            </div>
          </div>

          <Tutor
            avatar="cub-teal"
            say={
              orderDone
                ? 'Perfect! That is exactly the trip your message makes, every single time, in less than a second.'
                : checked
                  ? 'Not quite yet! Have a think about what happens first — before anything can travel, somebody has to press send.'
                  : 'Start with the very first thing that happens, and work forwards from there. Take your time.'
            }
          />

          <ol className="jr-order">
            {cards.map((c, i) => {
              const inPlace = checked && c.id === JOURNEY[i].id;
              const outOfPlace = checked && c.id !== JOURNEY[i].id;
              return (
                <li key={c.id} className={`${inPlace ? 'is-right' : ''} ${outOfPlace ? 'is-wrong' : ''}`}>
                  <span className="jr-order-n">{i + 1}</span>
                  <span className="jr-order-icon" aria-hidden="true">{c.icon}</span>
                  <span className="jr-order-text">{c.label}</span>
                  <span className="jr-order-moves">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || orderDone} aria-label="Move up">▲</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === cards.length - 1 || orderDone} aria-label="Move down">▼</button>
                  </span>
                </li>
              );
            })}
          </ol>

          {!orderDone && (
            <button type="button" className="btn btn-sm" onClick={check}>Check my order</button>
          )}

          {orderDone && (
            <div className="lab-done">
              <div>
                <strong>That is the journey! 🎉</strong>
                <p style={{ margin: '0.3rem 0 0' }}>Now send a message of your own and watch it get cut up.</p>
              </div>
              <button type="button" className="btn btn-sm" onClick={() => setTab('send')}>
                Send one yourself →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 2. send ---------------- */}
      {tab === 'send' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Send one yourself</h2>
              <p className="lab-lead">
                Type anything you like. Watch it get cut into packets, and see what order they turn
                up in at the other end.
              </p>
            </div>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setTab('order')}>
              ← Back to the order
            </button>
          </div>

          <Tutor
            avatar="cub-mint"
            say={
              sendDone
                ? 'You put them back together! That is exactly what your friend’s phone does — every time, without anybody noticing.'
                : sent
                  ? 'Look at the numbers. They arrived all jumbled up! Click them in the right order — 1, then 2, then 3 — to put your message back together.'
                  : 'Type a message and press send. Then watch very carefully.'
            }
          />

          <div className="jr-send">
            <label htmlFor="jr-msg">Your message</label>
            <input
              id="jr-msg"
              className="enc-input"
              value={text}
              maxLength={24}
              onChange={(e) => { setText(e.target.value); setSent(false); setRebuilt([]); }}
            />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => { setSent(true); setRebuilt([]); }}
            >
              📨 Send it
            </button>
          </div>

          {sent && (
            <>
              <div className="jr-step">
                <h3>1. It gets cut into packets</h3>
                <div className="jr-packets">
                  {packets.map((p) => (
                    <span key={p.n} className="jr-packet">
                      <b>{p.n}</b>
                      {p.part}
                    </span>
                  ))}
                </div>
              </div>

              <div className="jr-step">
                <h3>2. They arrive in a muddle</h3>
                <p className="enc-meta">Click them in the right order to put your message back together.</p>
                <div className="jr-packets">
                  {arriving.map((p) => (
                    <button
                      key={p.n}
                      type="button"
                      className={`jr-packet is-button ${rebuilt.some((r) => r.n === p.n) ? 'is-used' : ''}`}
                      onClick={() => addPacket(p)}
                      disabled={rebuilt.some((r) => r.n === p.n)}
                    >
                      <b>{p.n}</b>
                      {p.part}
                    </button>
                  ))}
                </div>
              </div>

              <div className="jr-step">
                <h3>3. Your friend sees</h3>
                <div className={`jr-result ${rebuiltRight ? 'is-right' : ''}`}>
                  {rebuilt.length === 0
                    ? '…'
                    : rebuilt.map((r) => r.part).join('')}
                </div>
                {rebuilt.length > 0 && !rebuiltRight && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRebuilt([])}>
                    Start again
                  </button>
                )}
              </div>
            </>
          )}

          {sendDone && (
            <div className="lab-done">
              <div>
                <strong>Message delivered! 🎉</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  One last bit — the most important one in the whole lesson.
                </p>
              </div>
              <button type="button" className="btn btn-sm" onClick={() => setTab('locks')}>
                Locked or not? →
              </button>
            </div>
          )}
        </section>
      )}

      {/* ---------------- 3. locks ---------------- */}
      {tab === 'locks' && (
        <section className="lab-section">
          <div className="place-head">
            <div>
              <h2>Locked or not?</h2>
              <p className="lab-lead">
                Your message goes past lots of computers that are not yours. The padlock keeps it
                secret. Here are four moments — what would you do?
              </p>
            </div>
            <div className="row gap-sm">
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setTab('send')}>
                ← Back to sending
              </button>
              <span className="pill">
                {LOCKS.filter((l) => lockPicks[l.id] === l.answer).length} / {LOCKS.length}
              </span>
            </div>
          </div>

          <Tutor
            avatar="cub-coral"
            say={
              locksDone
                ? 'Brilliant — especially the last one. The padlock means nobody can read your message on the way. It does not promise the website is nice. Those are two different things, and lots of grown-ups mix them up.'
                : 'Remember what the padlock actually does: it scrambles your message so other computers cannot read it. That is all it promises.'
            }
          />

          {LOCKS.map((l) => {
            const pick = lockPicks[l.id];
            const right = pick === l.answer;
            return (
              <div key={l.id} className={`lab-case ${right ? 'is-right' : pick !== undefined ? 'is-wrong' : ''}`}>
                <span className="lab-case-n" aria-hidden="true">{l.icon}</span>
                <div className="grow">
                  <p>{l.text}</p>
                  <div className="lab-choices">
                    {LOCK_OPTIONS.map((opt, oi) => (
                      <button
                        key={opt}
                        type="button"
                        className={`lab-choice ${pick === oi ? 'is-picked' : ''}`}
                        onClick={() => setLockPicks((p) => ({ ...p, [l.id]: oi }))}
                        disabled={right}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {pick !== undefined && (
                    <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                      {right
                        ? `✓ ${l.why}`
                        : l.tricky
                          ? '✗ This one is sneaky! Think again about what the padlock actually promises.'
                          : '✗ Not quite — have another go.'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {locksDone && (
            <div className="lab-done">
              <div>
                <strong>You did the whole thing! 🏆</strong>
                <p style={{ margin: '0.3rem 0 0' }}>
                  You can now explain how a message crosses the world — wires, packets, addresses,
                  servers and the padlock. Go and try it on a grown-up.
                </p>
              </div>
              <Link to="/learn/lesson/l1-how-the-internet-works" className="btn btn-sm">
                ← Back to the lesson
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
